import { removeSpace } from './common';

type BusinessNumberReturnType = {
  isValid: boolean;
  value: string;
};

type BusinessNumberTypes = {
  [key: string]: () => BusinessNumberReturnType;
};

export const australiaBusinessNumberFormatter = (
  number: string,
): BusinessNumberReturnType => {
  let copiedNumber = number;
  const input = number.replace(/\D/g, '').substring(0, 11); // First eleven digits of input only
  const field1 = input.substring(0, 2);
  const field2 = input.substring(2, 5);
  const field3 = input.substring(5, 8);
  const last = input.substring(8, 11);

  if (input.length > 8) {
    copiedNumber = `${field1} ${field2} ${field3} ${last}`;
  } else if (input.length > 5) {
    copiedNumber = `${field1} ${field2} ${field3}`;
  } else if (input.length > 2) {
    copiedNumber = `${field1} ${field2}`;
  } else {
    copiedNumber = `${field1}`;
  }

  return {
    isValid: removeSpace(copiedNumber).length === 11,
    value: copiedNumber,
  };
};

export const newZealandBusinessNumberFormat = (
  number: string,
): BusinessNumberReturnType => {
  return {
    isValid: removeSpace(number).length === 13,
    value: number.replace(/\D/g, '').substring(0, 13),
  };
};

export const unitedKingdomBusinessNumberFormat = (
  number: string,
): BusinessNumberReturnType => {
  const UK_BUSINESS_NUMBER_PATTERN = /(^[0-9]{8}$)|(^SC[0-9]{6}$)/g;

  return {
    isValid: number.match(UK_BUSINESS_NUMBER_PATTERN) != null,
    value: number.replace(/[^0-9SC]/g, '').substring(0, 8),
  };
};

export const franceBusinessNumberFormat = (
  number: string,
): BusinessNumberReturnType => {
  return {
    isValid:
      removeSpace(number).length === 9 || removeSpace(number).length === 14,
    value: number.replace(/\D/g, '').substring(0, 14),
  };
};

export const unitedStateBusinessNumberFormat = (
  number: string,
): BusinessNumberReturnType => {
  let copiedNumber = number;

  const input = number.replace(/\D/g, '').substring(0, 9);
  const field1 = input.substring(0, 2);
  const field2 = input.substring(2, 9);

  if (input.length > 2) {
    copiedNumber = `${field1}-${field2}`;
  } else {
    copiedNumber = `${field1}`;
  }

  return { isValid: removeSpace(input).length === 9, value: copiedNumber };
};

export const switzerlandNumberFormat = (
  number: string,
): BusinessNumberReturnType => {
  let copiedNumber = number;

  const input = number.replace(/\D/g, '').substring(0, 12);
  const field1 = input.substring(0, 3);
  const field2 = input.substring(3, 6);
  const field3 = input.substring(6, 9);

  if (input.length > 6) {
    copiedNumber = `${field1} ${field2} ${field3}`;
  } else if (input.length > 3) {
    copiedNumber = `${field1} ${field2}`;
  } else {
    copiedNumber = `${field1}`;
  }

  return {
    isValid: removeSpace(copiedNumber).length === 9,
    value: 'CHE ' + copiedNumber,
  };
};

/**
 *
 * @param country The country is a string type to be use for selecting type of business format
 * @param number The number is a string type to be use for formatting
 * @returns The businessNumberFormatter returns string with different format base on selected country
 */
export const businessNumberFormatter = (
  country: string,
  number: string,
): BusinessNumberReturnType => {
  const businessNumberByCountry: BusinessNumberTypes = {
    AU: () => australiaBusinessNumberFormatter(number),
    CH: () => switzerlandNumberFormat(number),
    FR: () => franceBusinessNumberFormat(number),
    GB: () => unitedKingdomBusinessNumberFormat(number),
    NZ: () => newZealandBusinessNumberFormat(number),
    US: () => unitedStateBusinessNumberFormat(number),
  };

  if (businessNumberByCountry[country] == null)
    return {
      isValid: removeSpace(number).length < 21,
      value: number.replace(/\D/g, '').slice(0, 21),
    };

  return businessNumberByCountry[country]();
};
